import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  BarChart3,
  Boxes,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  FileText,
  Landmark,
  LockKeyhole,
  MessageSquareText,
  PackageCheck,
  PieChart,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Store,
  UsersRound,
  WalletCards,
} from "lucide-react";

import Footer from "./Footer";
import "./Features.css";

import invoicingImg from "../components/Images/pricing.png";
import reportsImg from "../components/Images/report.png";
import accountingImg from "../components/Images/collabo.png";
import collaborationImg from "../components/Images/remote-access.png";
import aiGraphic from "../components/Images/AI-Image.webp";

const fadeInUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const heroStats = [
  "Invoices",
  "Receipts",
  "Inventory",
  "Payroll",
  "Reports",
  "Team Roles",
];

const benefits = [
  {
    icon: ReceiptText,
    title: "Send invoices faster",
    text: "Create neat, branded invoices and receipts without struggling with Word or Excel.",
  },
  {
    icon: WalletCards,
    title: "Know your cash position",
    text: "See what came in, what went out, who owes you, and what you still need to pay.",
  },
  {
    icon: UsersRound,
    title: "Work safely with your team",
    text: "Give each person access to only the areas they need for their role.",
  },
  {
    icon: ShieldCheck,
    title: "Stay better organised",
    text: "Keep customers, vendors, payroll, stock, assets, reports, and records in one place.",
  },
];

const featureSections = [
  {
    id: "sales",
    eyebrow: "Sales & invoicing",
    title: "Create invoices, estimates, receipts, and track payments easily.",
    copy: "KostPadi helps you manage the full sales journey — from sending a quote to collecting payment. You can prepare clean customer documents, track paid and unpaid invoices, and know exactly which customers still owe your business.",
    image: invoicingImg,
    icon: FileText,
    bullets: [
      "Create professional invoices, estimates, and receipts",
      "Convert approved estimates into invoices",
      "Track paid, unpaid, pending, and overdue invoices",
      "Record customer payments and balances",
      "Download clean PDF documents for customers",
      "Support for VAT and WHT where applicable",
    ],
  },
  {
    id: "accounting",
    eyebrow: "Accounting & bookkeeping",
    title: "Keep proper books without getting lost in accounting jargon.",
    copy: "KostPadi gives your business a clean bookkeeping system. Your accountant can manage journals, ledgers, chart of accounts, and financial records while you focus on understanding the numbers that matter.",
    image: accountingImg,
    icon: Landmark,
    reverse: true,
    bullets: [
      "Chart of accounts automatically created for companies",
      "Record journal entries and business transactions",
      "View general ledger and account balances",
      "Generate trial balance",
      "Track accounts receivable and accounts payable",
      "Cleaner records for your accountant, auditor, or tax consultant",
    ],
  },
  {
    id: "reports",
    eyebrow: "Financial reports",
    title: "Understand your business performance at a glance.",
    copy: "You do not need to wait till year-end to know how your business is doing. KostPadi gives you practical reports that show profit, cash flow, assets, liabilities, and business performance.",
    image: reportsImg,
    icon: BarChart3,
    bullets: [
      "Income statement to see profit or loss",
      "Balance sheet to understand business position",
      "Cash flow report to see movement of money",
      "Management report for business review",
      "Customer and vendor aging reports",
      "Export reports to PDF, Excel, or CSV where available",
    ],
  },
  {
    id: "team",
    eyebrow: "Team access & user roles",
    title:
      "Let your team work in KostPadi without giving everyone full access.",
    copy: "As your business grows, different people need different access. KostPadi now supports dynamic user roles so your accountant, sales staff, HR, marketing team, and admins can each see the features relevant to their work.",
    image: collaborationImg,
    icon: UsersRound,
    reverse: true,
    bullets: [
      "Super Admin access for business owners or main admins",
      "Accountant access for finance and bookkeeping work",
      "Sales/user access for invoice and customer-related tasks",
      "HR access for payroll and employee workflows",
      "Marketing access for CRM and customer engagement work",
      "Sidebar automatically shows only what each user can use",
    ],
  },
];

const moduleCards = [
  {
    icon: Boxes,
    title: "Inventory management",
    text: "Track products, stock movement, warehouses, stock value, and ageing inventory so you know what is available and what needs attention.",
  },
  {
    icon: PackageCheck,
    title: "Purchases & expenses",
    text: "Record vendor bills, purchases, supplier payments, operating expenses, and know what your business still owes.",
  },
  {
    icon: Building2,
    title: "Fixed asset register",
    text: "Track business assets like laptops, equipment, furniture, and vehicles, including depreciation and asset history.",
  },
  {
    icon: Banknote,
    title: "Bank & cash management",
    text: "Record bank transactions, track cash movements, and reconcile your records so your books agree with your bank.",
  },
  {
    icon: BriefcaseBusiness,
    title: "CRM tool",
    text: "Keep customer details, interactions, follow-ups, and business opportunities organised in one place.",
  },
  {
    icon: UsersRound,
    title: "Employee payroll management",
    text: "Manage employees, payroll runs, payslips, approvals, deductions, and payroll posting into your accounting records.",
  },
  {
    icon: PieChart,
    title: "Budgeting, FP&A",
    text: "Plan business budgets, compare actual results against plans, and understand where your money is going.",
  },
  {
    icon: CalendarCheck,
    title: "Booking & scheduling",
    text: "Manage bookings, appointments, task schedules, and business activities so your team stays coordinated.",
  },
  {
    icon: ClipboardList,
    title: "Tasks & approvals",
    text: "Assign work, monitor progress, add comments, and keep approval workflows clearer across your team.",
  },
];

const roleCards = [
  {
    title: "Super Admin",
    text: "Best for business owners or top-level admins who need full control.",
  },
  {
    title: "Accountant",
    text: "Best for users handling accounting, reports, journals, and financial records.",
  },
  {
    title: "Sales/User",
    text: "Best for staff who manage customers, invoices, receipts, and sales activity.",
  },
  {
    title: "HR User",
    text: "Best for employee records, payroll workflows, and people-related tasks.",
  },
  {
    title: "Marketing User",
    text: "Best for CRM, customer engagement, campaigns, and follow-up activities.",
  },
];

const audienceCards = [
  "SMEs",
  "Retail stores",
  "Consultants",
  "Agencies",
  "Startups",
  "NGOs",
  "Service businesses",
  "Growing finance teams",
];

const whyCards = [
  "No more scattered spreadsheets",
  "Know who owes your business",
  "Track stock and business assets",
  "Work easily with your accountant",
  "Manage team access safely",
  "Get clearer reports for decisions",
];

const faqs = [
  {
    q: "Is KostPadi only for accountants?",
    a: "No. KostPadi is designed for business owners, finance teams, sales teams, HR teams, and accountants. The language and workflows are simple enough for non-accountants.",
  },
  {
    q: "Can I invite my staff?",
    a: "Yes. You can invite team members and assign roles so each person only sees what is relevant to their work.",
  },
  {
    q: "Does KostPadi support Nigerian businesses?",
    a: "Yes. KostPadi supports practical Nigerian business needs like VAT, WHT, invoices, receipts, customer balances, vendor payments, payroll, and financial reports.",
  },
  {
    q: "Can I export my reports?",
    a: "Yes. KostPadi supports downloadable documents and export-friendly reports such as PDF, Excel, and CSV where available.",
  },
];

export default function Features() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <div className="features-page">
      <header className="features-hero" id="top">
        <div className="kp-container features-hero-grid">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="features-hero-copy"
          >
            <motion.span variants={fadeInUp} className="features-tag">
              Built for growing businesses
            </motion.span>

            <motion.h1 variants={fadeInUp}>
              Run your business finance in one simple platform.
            </motion.h1>

            <motion.p variants={fadeInUp} className="features-lead">
              Create invoices, manage customers, track expenses, monitor
              inventory, run payroll, generate reports, and collaborate with
              your team — without complicated accounting software.
            </motion.p>

            <motion.div variants={fadeInUp} className="features-hero-pills">
              {heroStats.map((item) => (
                <span key={item}>
                  <CheckCircle2 size={16} />
                  {item}
                </span>
              ))}
            </motion.div>

            <motion.div variants={fadeInUp} className="features-hero-actions">
              <a className="features-btn features-btn-primary" href="/login">
                Start Free <ArrowRight size={18} />
              </a>
              <a className="features-btn features-btn-ghost" href="/pricing">
                View Pricing
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="features-hero-card"
          >
            <img src={aiGraphic} alt="KostPadi business finance dashboard" />

            <div className="features-floating-card card-one">
              <BadgeCheck size={18} />
              <span>Invoices tracked</span>
            </div>

            <div className="features-floating-card card-two">
              <BarChart3 size={18} />
              <span>Reports ready</span>
            </div>
          </motion.div>
        </div>
      </header>

      <main>
        <section className="kp-container features-benefit-section">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="features-benefit-grid"
          >
            {benefits.map(({ icon: Icon, title, text }) => (
              <motion.div
                variants={fadeInUp}
                className="features-benefit-card"
                key={title}
              >
                <div className="features-icon-wrap">
                  <Icon size={22} />
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        <section className="kp-container features-layout">
          <aside className="features-toc">
            <a href="#sales">Sales</a>
            <a href="#accounting">Accounting</a>
            <a href="#reports">Reports</a>
            <a href="#team">Team Roles</a>
            <a href="#modules">More Tools</a>
            <a href="#roles">User Types</a>
            <a href="#ai">AI Assistant</a>
            <a href="#faq">FAQ</a>
          </aside>

          <div className="features-content">
            {featureSections.map((feature) => (
              <FeatureRow key={feature.id} {...feature} />
            ))}

            <section id="modules" className="features-section">
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="features-section-head"
              >
                <span className="features-mini-tag">Everything included</span>
                <h2>More tools to manage your business properly.</h2>
                <p>
                  KostPadi brings your daily finance and operations work into
                  one workspace, so your business is not depending on scattered
                  notebooks, WhatsApp messages, and spreadsheets.
                </p>
              </motion.div>

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="features-module-grid"
              >
                {moduleCards.map(({ icon: Icon, title, text }) => (
                  <motion.div
                    variants={fadeInUp}
                    className="features-module-card"
                    key={title}
                  >
                    <div className="features-icon-wrap soft">
                      <Icon size={22} />
                    </div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </motion.div>
                ))}
              </motion.div>
            </section>

            <section
              id="roles"
              className="features-section features-role-section"
            >
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="features-section-head"
              >
                <span className="features-mini-tag">Multi-user access</span>
                <h2>Give every team member the right access.</h2>
                <p>
                  Not everyone in your business should see everything. KostPadi
                  helps you keep things organised by showing each user only the
                  tools they need to do their job.
                </p>
              </motion.div>

              <div className="features-role-grid">
                {roleCards.map((role) => (
                  <motion.div
                    key={role.title}
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="features-role-card"
                  >
                    <LockKeyhole size={20} />
                    <h3>{role.title}</h3>
                    <p>{role.text}</p>
                  </motion.div>
                ))}
              </div>
            </section>

            <section id="ai" className="features-section features-ai-section">
              <motion.div
                variants={fadeIn}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="features-ai-card"
              >
                <div className="features-ai-copy">
                  <span className="features-mini-tag coming">Coming soon</span>
                  <h2>AI Business Assistant</h2>
                  <p>
                    KostPadi’s AI assistant will help you ask business questions
                    in plain English and get useful answers from your numbers.
                    For example: “Who owes me the most?”, “How much profit did I
                    make this month?”, or “Which expense is increasing?”
                  </p>

                  <div className="features-ai-list">
                    <span>
                      <Sparkles size={16} /> Plain-English financial questions
                    </span>
                    <span>
                      <MessageSquareText size={16} /> Smart business summaries
                    </span>
                    <span>
                      <BarChart3 size={16} /> Helpful insights from your records
                    </span>
                  </div>
                </div>

                <img src={aiGraphic} alt="KostPadi AI assistant preview" />
              </motion.div>
            </section>

            <section className="features-section features-audience-section">
              <div className="features-section-head">
                <span className="features-mini-tag">Who it is for</span>
                <h2>
                  KostPadi works for different types of growing businesses.
                </h2>
                <p>
                  Whether you sell products, offer services, manage projects, or
                  run an organisation, KostPadi helps you stay financially
                  organised.
                </p>
              </div>

              <div className="features-audience-grid">
                {audienceCards.map((item) => (
                  <span key={item}>
                    <Store size={17} />
                    {item}
                  </span>
                ))}
              </div>
            </section>

            <section className="features-section features-why-section">
              <div className="features-section-head">
                <span className="features-mini-tag">
                  Why businesses choose KostPadi
                </span>
                <h2>Less confusion. Better records. Clearer decisions.</h2>
              </div>

              <div className="features-why-grid">
                {whyCards.map((item) => (
                  <div className="features-why-card" key={item}>
                    <CheckCircle2 size={19} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="features-section features-cta-section">
              <div className="features-cta-card">
                <div>
                  <h2>Ready to make your business finance easier?</h2>
                  <p>
                    Start with the tools you need today, then upgrade as your
                    business grows.
                  </p>
                </div>

                <div className="features-cta-actions">
                  <a
                    className="features-btn features-btn-primary"
                    href="/login"
                  >
                    Start Free <ArrowRight size={18} />
                  </a>
                  <a
                    className="features-btn features-btn-ghost light"
                    href="/pricing"
                  >
                    See Plans
                  </a>
                </div>
              </div>
            </section>

            <section id="faq" className="features-section features-faq-section">
              <div className="features-section-head">
                <span className="features-mini-tag">Questions</span>
                <h2>Frequently asked questions</h2>
              </div>

              <div className="features-faq-list">
                {faqs.map((faq) => (
                  <div className="features-faq-item" key={faq.q}>
                    <h3>{faq.q}</h3>
                    <p>{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function FeatureRow({
  id,
  eyebrow,
  title,
  copy,
  bullets,
  image,
  icon: Icon,
  reverse,
}) {
  return (
    <section id={id} className={`features-row ${reverse ? "reverse" : ""}`}>
      <motion.div
        className="features-row-copy"
        variants={fadeInUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
      >
        <span className="features-mini-tag">
          <Icon size={16} />
          {eyebrow}
        </span>

        <h2>{title}</h2>
        <p>{copy}</p>

        <ul>
          {bullets.map((bullet) => (
            <li key={bullet}>
              <CheckCircle2 size={18} />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div
        className="features-row-media"
        variants={fadeIn}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
      >
        <img src={image} alt={title} />
      </motion.div>
    </section>
  );
}
